import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import ClientLayout from './components/ClientLayout';
import WhatsAppButton from './components/WhatsAppButton';
import InstallPWA from './components/InstallPWA';
import DynamicManifest from './components/DynamicManifest';
import SplashScreen from './components/SplashScreen';
import { BRAND } from './brand';
// === EAGER (carga inicial — flujos del cliente final + landing + auth) ===
import LandingBrand from './pages/LandingGirosBol';
import LoginApp from './pages/LoginApp';
import DashboardApp from './pages/DashboardApp';
import Register from './pages/Register';
import RegisterNew from './pages/RegisterNew';
import Dashboard from './pages/Dashboard';
import Cotizar from './pages/Cotizar';
import PublicRates from './pages/PublicRates';
import Privacidad from './pages/Privacidad';
import Terminos from './pages/Terminos';
import Cumplimiento from './pages/Cumplimiento';
import EliminarCuenta from './pages/EliminarCuenta';
import MiBiometria from './pages/MiBiometria';
import TrackOrder from './pages/TrackOrder';
import VerifyEmail from './pages/VerifyEmail';
import GBPuntosClub from './pages/GBPuntosClub';
// Módulo Empresas v1 (Carlos jun 28, 2026)
import EmpresaRegistro from './pages/EmpresaRegistro';
import EmpresaLogin from './pages/EmpresaLogin';
import EmpresaDocs from './pages/EmpresaDocs';
import EmpresaPanel from './pages/EmpresaPanel';
import EmpresaComprobante from './pages/EmpresaComprobante';
import AdminEmpresas from './pages/AdminEmpresas';
import SendUnavailable from './pages/SendUnavailable';

// === LAZY (carga bajo demanda — admin + flujos raros) ===
// Resultado: bundle inicial cae de ~3.3MB a ~1.5MB.
const InstagramStories = lazy(() => import('./pages/InstagramStories'));
const InstagramStoryDownload = lazy(() => import('./pages/InstagramStoryDownload'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));
const AdminUafAlerts = lazy(() => import('./pages/AdminUafAlerts'));
const AdminUafRequirements = lazy(() => import('./pages/AdminUafRequirements'));
const AdminTransfers = lazy(() => import('./pages/AdminTransfers'));
const AdminExchange = lazy(() => import('./pages/AdminExchange'));
const AdminBobToClp = lazy(() => import('./pages/AdminBobToClp'));
const AdminBobCommissions = lazy(() => import('./pages/AdminBobCommissions'));
const AdminRecharges = lazy(() => import('./pages/AdminRecharges'));
const Recharge = lazy(() => import('./pages/Recharge'));
const AdminCreateUser = lazy(() => import('./pages/AdminCreateUser'));
const AdminCreateAdmin = lazy(() => import('./pages/AdminCreateAdmin'));
const AdminUserProfile = lazy(() => import('./pages/AdminUserProfile'));
const AdminReports = lazy(() => import('./pages/AdminReports'));
const AdminAnalytics = lazy(() => import('./pages/AdminAnalytics'));
const AdminVisitors = lazy(() => import('./pages/AdminVisitors'));
const AdminUAF = lazy(() => import('./pages/AdminUAF'));
const AdminUAFROS = lazy(() => import('./pages/AdminUAFROS'));
const AdminUAFROSForm = lazy(() => import('./pages/AdminUAFROSForm'));
const AdminUAFRisk = lazy(() => import('./pages/AdminUAFRisk'));
const AdminUAFPep = lazy(() => import('./pages/AdminUAFPep'));
const AdminTax = lazy(() => import('./pages/AdminTax'));
const AdminFunnel = lazy(() => import('./pages/AdminFunnel'));
const DemoSuccessAnimation = lazy(() => import('./pages/DemoSuccessAnimation'));
const AdminBoletas = lazy(() => import('./pages/AdminBoletas'));
const AdminWhatsAppCampaigns = lazy(() => import('./pages/AdminWhatsAppCampaigns'));
const Complaints = lazy(() => import('./pages/Complaints'));
const AdminComplaints = lazy(() => import('./pages/AdminComplaints'));
const AdminWallets = lazy(() => import('./pages/AdminWallets'));
const AdminCalculator = lazy(() => import('./pages/AdminCalculator'));
const PitchDeck = lazy(() => import('./pages/PitchDeck'));
const CrediBol = lazy(() => import('./pages/CrediBol'));
const AdminCrediBol = lazy(() => import('./pages/AdminCrediBol'));
const AdminTransferOrders = lazy(() => import('./pages/AdminTransferOrders'));
const AdminOtherCountries = lazy(() => import('./pages/AdminOtherCountries'));
const AdminSettings = lazy(() => import('./pages/AdminSettings'));
const AdminCashPayment = lazy(() => import('./pages/AdminCashPayment'));
const AdminAuditLog = lazy(() => import('./pages/AdminAuditLog'));
const AdminFraudAlerts = lazy(() => import('./pages/AdminFraudAlerts'));
const AdminDashboardPro = lazy(() => import('./pages/AdminDashboardPro'));
const AdminSorteos = lazy(() => import('./pages/AdminSorteos'));
const AdminWhatsApp = lazy(() => import('./pages/AdminWhatsApp'));
const AdminWhatsAppCloudTest = lazy(() => import('./pages/AdminWhatsAppCloudTest'));
const AdminWhatsAppInbox = lazy(() => import('./pages/AdminWhatsAppInbox'));
const AdminGBPuntos = lazy(() => import('./pages/AdminGBPuntos'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const SendMoneyP2PFinal = lazy(() => import('./pages/SendMoneyP2PFinal'));
const SendPEN = lazy(() => import('./pages/SendPEN'));
const SendCLP = lazy(() => import('./pages/SendCLP'));
const AdminPenToClp = lazy(() => import('./pages/AdminPenToClp'));
const TransferStatus = lazy(() => import('./pages/TransferStatus'));
const TransferOrderDetail = lazy(() => import('./pages/TransferOrderDetail'));
const ConvertCurrency = lazy(() => import('./pages/ConvertCurrency'));
const KYCSubmit = lazy(() => import('./pages/KYCSubmit'));
const KycSelfService = lazy(() => import('./pages/KycSelfService'));
const VerificarIdentidad = lazy(() => import('./pages/VerificarIdentidad'));
const DeclaracionJurada = lazy(() => import('./pages/DeclaracionJurada'));
const ActualizarKYC = lazy(() => import('./pages/ActualizarKYC'));
const ProfileApp = lazy(() => import('./pages/ProfileApp'));
const Transactions = lazy(() => import('./pages/Transactions'));
const Recipients = lazy(() => import('./pages/Recipients'));
import './App.css';

// Componente que redirige según el tipo de usuario
const SmartRedirect = ({ to }) => {
  const { user } = useAuth();
  
  // Si es admin y trata de ir a rutas de cliente, redirigir a admin
  if (user?.is_admin && (to === '/dashboard' || to === '/')) {
    return <Navigate to="/admin" replace />;
  }
  
  return <Navigate to={to} replace />;
};

// Gate por feature flag del preset. Si el flag está en false, redirige al dashboard.
// Uso: <FeatureRoute feature="gbPuntos"><GBPuntosClub /></FeatureRoute>
const FeatureRoute = ({ feature, children, fallback = '/dashboard' }) => {
  if (!BRAND?.features?.[feature]) return <Navigate to={fallback} replace />;
  return children;
};

// Gate por PAR de corredor consultando /api/config/corridors en runtime.
// Fail-closed: mientras carga, muestra spinner; si el par no está activo,
// redirige al fallback. No usa el default de la marca — sólo lo real de BD.
const CorridorPairRoute = ({ pair, children, fallback = '/dashboard' }) => {
  const [state, setState] = React.useState('loading');
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${backend}/api/config/corridors`);
        const d = await r.json();
        const on = !!(d?.corridors && d.corridors[pair]);
        if (alive) setState(on ? 'ok' : 'off');
      } catch (_e) {
        if (alive) setState('off');
      }
    })();
    return () => { alive = false; };
  }, [pair]);
  if (state === 'loading') {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{borderColor: 'var(--brand-primary)'}}></div></div>;
  }
  if (state === 'off') return <Navigate to={fallback} replace />;
  return children;
};

// Gate por corredor activo. Bloquea superficies con lógica de cotización hardcodeada
// a una moneda (ej. flujos Bolivia legacy) si esa moneda no está en los corredores
// activos de la marca. Sirve como fail-closed: preferimos no renderizar antes que
// cotizar con datos de otra marca.
const CorridorRoute = ({ currency, children, fallback = '/send/unavailable' }) => {
  const active = (BRAND?.exchange?.corridors || []).some(
    (c) => c && c.active && c.currency === currency,
  );
  if (!active) return <Navigate to={fallback} replace />;
  return children;
};

// Ruta protegida solo para admins
const AdminRoute = ({ children }) => {
  const { user, loading, connectionError, retryConnection } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Si hay error de conexión y NO hay usuario cacheado, mostrar pantalla de reconexión
  if (connectionError && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-sm mx-4">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">Conectando al servidor...</h2>
          <p className="text-sm text-gray-500 mb-4">Esto tomará unos segundos</p>
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div className="bg-green-600 h-1.5 rounded-full animate-pulse" style={{width: '60%'}}></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Ruta protegida solo para clientes (no admin)
const ClientRoute = ({ children, noLayout }) => {
  const { user, loading, connectionError, retryConnection } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (connectionError && !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-sm mx-4">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-yellow-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <h2 className="text-lg font-bold text-gray-800 mb-2">Conectando al servidor...</h2>
          <p className="text-sm text-gray-500 mb-4">Esto tomará unos segundos</p>
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div className="bg-green-600 h-1.5 rounded-full animate-pulse" style={{width: '60%'}}></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Si es admin, redirigir al panel de admin
  if (user.is_admin) {
    return <Navigate to="/admin" replace />;
  }
  
  if (noLayout) return children;
  return <ClientLayout>{children}</ClientLayout>;
};

// Componente de login inteligente
const SmartLogin = () => {
  const { user } = useAuth();
  
  if (user) {
    // Si ya está logueado, redirigir según su rol
    if (user.is_admin) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return <LoginApp />;
};

// Detectar si es PWA instalada → directo al login
const SmartLanding = () => {
  const { user } = useAuth();
  
  // Si ya está logueado, ir al dashboard
  if (user) {
    return user.is_admin ? <Navigate to="/admin" replace /> : <Navigate to="/dashboard" replace />;
  }
  
  // Detectar si es PWA instalada (standalone mode)
  const isPWA = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone === true
    || document.referrer.includes('android-app://');
  
  if (isPWA) {
    return <Navigate to="/login" replace />;
  }
  
  // Web normal → mostrar landing
  return <LandingBrand />;
};

function PromoRedirect() {
  const { promoCode } = useParams();
  // Validamos primero contra el sistema de referidos GB Puntos.
  // Si es un código de referido válido → /register?ref=XXX
  // Si no → asumimos campaña de marketing → /register?promo=XXX
  const [target, setTarget] = React.useState(null);
  React.useEffect(() => {
    const code = (promoCode || '').toUpperCase();
    if (!code) { setTarget('/register'); return; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gb/validate-referral/${code}`)
      .then(r => r.json())
      .then(data => {
        if (data?.valid) {
          setTarget(`/register?ref=${code}`);
        } else {
          setTarget(`/register?promo=${code}`);
        }
      })
      .catch(() => setTarget(`/register?promo=${code}`));
  }, [promoCode]);
  if (target === null) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin h-8 w-8 border-4 border-green-600 border-t-transparent rounded-full" />
      </div>
    );
  }
  return <Navigate to={target} replace />;
}

function AppRoutes() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    }>
      <Routes>
      {/* Rutas públicas */}
      <Route path="/" element={<SmartLanding />} />
      <Route path="/cotizar" element={<Cotizar />} />
      <Route
        path="/admin/presentacion"
        element={
          <AdminRoute>
            <FeatureRoute feature="pitchDeck" fallback="/admin"><PitchDeck /></FeatureRoute>
          </AdminRoute>
        }
      />
      <Route path="/instagram-stories" element={<InstagramStories />} />
      <Route path="/descargar-historias" element={<InstagramStoryDownload />} />
      <Route path="/login" element={<SmartLogin />} />
      <Route path="/register" element={<RegisterNew />} />
      <Route path="/register-old" element={<Register />} />
      <Route path="/r/:promoCode" element={<PromoRedirect />} />
      <Route path="/verificar/:token" element={<VerificarIdentidad />} />
      <Route path="/declaracion-jurada/:token" element={<DeclaracionJurada />} />
      <Route path="/actualizar-kyc/:token" element={<ActualizarKYC />} />
      
      {/* Ruta pública de seguimiento de orden */}
      <Route path="/track/:folio" element={<TrackOrder />} />
      
      {/* Ruta pública de tasas de cambio */}
      <Route path="/tasas" element={<PublicRates />} />
      
      {/* Rutas de clientes (bloqueadas para admin) */}
      <Route
        path="/dashboard"
        element={
          <ClientRoute>
            <DashboardApp />
          </ClientRoute>
        }
      />
      <Route
        path="/send"
        element={
          <ClientRoute>
            <CorridorRoute currency="PEN">
              <SendPEN />
            </CorridorRoute>
          </ClientRoute>
        }
      />
      <Route
        path="/send-money"
        element={
          <ClientRoute>
            <CorridorRoute currency="PEN">
              <SendPEN />
            </CorridorRoute>
          </ClientRoute>
        }
      />
      <Route
        path="/send-clp"
        element={
          <ClientRoute>
            <CorridorPairRoute pair="PEN-CLP" fallback="/send/unavailable">
              <SendCLP />
            </CorridorPairRoute>
          </ClientRoute>
        }
      />
      <Route
        path="/send/unavailable"
        element={
          <ClientRoute>
            <SendUnavailable />
          </ClientRoute>
        }
      />
      <Route
        path="/convert"
        element={
          <ClientRoute>
            <FeatureRoute feature="wallet"><ConvertCurrency /></FeatureRoute>
          </ClientRoute>
        }
      />
      <Route
        path="/transfer-status/:transactionId"
        element={
          <ClientRoute>
            <TransferStatus />
          </ClientRoute>
        }
      />
      <Route
        path="/transfer-order/:orderId"
        element={
          <ClientRoute>
            <TransferOrderDetail />
          </ClientRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <ClientRoute>
            <Transactions />
          </ClientRoute>
        }
      />
      <Route
        path="/recharge"
        element={
          <ClientRoute>
            <FeatureRoute feature="wallet"><Recharge /></FeatureRoute>
          </ClientRoute>
        }
      />
      <Route
        path="/gb-puntos"
        element={
          <ClientRoute>
            <FeatureRoute feature="gbPuntos"><GBPuntosClub /></FeatureRoute>
          </ClientRoute>
        }
      />
      <Route
        path="/recipients"
        element={
          <ClientRoute>
            <Recipients />
          </ClientRoute>
        }
      />
      <Route
        path="/kyc"
        element={
          <ClientRoute noLayout>
            <KYCSubmit />
          </ClientRoute>
        }
      />
      <Route
        path="/kyc-verify"
        element={
          <ClientRoute noLayout>
            <KycSelfService />
          </ClientRoute>
        }
      />
      
      <Route
        path="/verify-email"
        element={
          <ClientRoute noLayout>
            <VerifyEmail />
          </ClientRoute>
        }
      />

      {/* Rutas de admin (solo para administradores) */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminPanel />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/transfers"
        element={
          <AdminRoute>
            <AdminTransfers />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/exchange"
        element={
          <AdminRoute>
            <AdminExchange />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/bob-to-clp"
        element={
          <AdminRoute>
            <AdminBobToClp />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/pen-to-clp"
        element={
          <AdminRoute>
            <AdminPenToClp />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/bob-commissions"
        element={
          <AdminRoute>
            <AdminBobCommissions />
          </AdminRoute>
        }
      />
      {/* KYC Review route removed - functionality moved to AdminPanel */}
      {/* AdminUafLifecycle removed by user request (feb-2026) */}
      <Route
        path="/admin/recharges"
        element={
          <AdminRoute>
            <FeatureRoute feature="wallet" fallback="/admin"><AdminRecharges /></FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/create-user"
        element={
          <AdminRoute>
            <AdminCreateUser />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/create-admin"
        element={
          <AdminRoute>
            <AdminCreateAdmin />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/user/:userId"
        element={
          <AdminRoute>
            <AdminUserProfile />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <AdminRoute>
            <AdminAnalytics />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/visitas"
        element={
          <AdminRoute>
            <AdminVisitors />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/uaf"
        element={
          <AdminRoute>
            <AdminUAF />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/uaf/ros"
        element={
          <AdminRoute>
            <AdminUAFROS />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/uaf/ros/nuevo"
        element={
          <AdminRoute>
            <AdminUAFROSForm />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/uaf/ros/:id"
        element={
          <AdminRoute>
            <AdminUAFROSForm />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/uaf/riesgo"
        element={
          <AdminRoute>
            <AdminUAFRisk />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/uaf/pep"
        element={
          <AdminRoute>
            <AdminUAFPep />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/uaf/alertas"
        element={
          <AdminRoute>
            <AdminUafAlerts />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/uaf/requerimientos"
        element={
          <AdminRoute>
            <AdminUafRequirements />
          </AdminRoute>
        }
      />
      <Route path="/cumplimiento" element={<Cumplimiento />} />
      {/* Módulo Empresas v1 (Carlos jun 28, 2026) */}
      <Route path="/empresas/registro" element={<EmpresaRegistro />} />
      <Route path="/empresas/login" element={<EmpresaLogin />} />
      <Route path="/empresas/docs" element={<EmpresaDocs />} />
      <Route path="/empresas/panel" element={<CorridorRoute currency="BOB"><EmpresaPanel /></CorridorRoute>} />
      <Route path="/empresas/comprobante/:orderId" element={<CorridorRoute currency="BOB"><EmpresaComprobante /></CorridorRoute>} />
      <Route
        path="/admin/empresas"
        element={
          <AdminRoute>
            <CorridorRoute currency="BOB" fallback="/admin"><AdminEmpresas /></CorridorRoute>
          </AdminRoute>
        }
      />
      <Route path="/demo/success-animation" element={<DemoSuccessAnimation />} />
      <Route
        path="/admin/tax"
        element={
          <AdminRoute>
            <AdminTax />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/boletas"
        element={
          <AdminRoute>
            <AdminBoletas />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/funnel"
        element={
          <AdminRoute>
            <AdminFunnel />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/whatsapp-campaigns"
        element={
          <AdminRoute>
            <AdminWhatsAppCampaigns />
          </AdminRoute>
        }
      />
      <Route path="/denuncias" element={<Complaints />} />
      <Route path="/privacidad" element={<Privacidad />} />
      <Route path="/terminos" element={<Terminos />} />
      <Route path="/eliminar-cuenta" element={<EliminarCuenta />} />
      <Route path="/eliminar-datos" element={<EliminarCuenta />} />
      <Route path="/delete-account" element={<EliminarCuenta />} />
      <Route
        path="/mi-biometria"
        element={
          <ClientRoute>
            <MiBiometria />
          </ClientRoute>
        }
      />
      <Route
        path="/credibol"
        element={
          <ClientRoute>
            <FeatureRoute feature="credibol"><CrediBol /></FeatureRoute>
          </ClientRoute>
        }
      />
      <Route
        path="/admin/credibol"
        element={
          <AdminRoute>
            <FeatureRoute feature="credibol" fallback="/admin"><AdminCrediBol /></FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/denuncias"
        element={
          <AdminRoute>
            <AdminComplaints />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/wallets"
        element={
          <AdminRoute>
            <FeatureRoute feature="wallet" fallback="/admin"><AdminWallets /></FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/calculadora"
        element={
          <AdminRoute>
            <AdminCalculator />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <AdminRoute>
            <AdminReports />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/transfer-orders"
        element={
          <AdminRoute>
            <AdminTransferOrders />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/dashboard-pro"
        element={
          <AdminRoute>
            <AdminDashboardPro />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/cash-payment"
        element={
          <AdminRoute>
            <AdminCashPayment />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/audit-log"
        element={
          <AdminRoute>
            <AdminAuditLog />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/other-countries"
        element={
          <AdminRoute>
            <AdminOtherCountries />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <AdminRoute>
            <AdminSettings />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/fraud-alerts"
        element={
          <AdminRoute>
            <AdminFraudAlerts />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/sorteos"
        element={
          <AdminRoute>
            <FeatureRoute feature="sorteo" fallback="/admin"><AdminSorteos /></FeatureRoute>
          </AdminRoute>
        }
      />
      <Route
        path="/admin/whatsapp"
        element={
          <AdminRoute>
            <AdminWhatsApp />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/whatsapp-cloud-test"
        element={
          <AdminRoute>
            <AdminWhatsAppCloudTest />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/whatsapp-inbox"
        element={
          <AdminRoute>
            <AdminWhatsAppInbox />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/gb-puntos"
        element={
          <AdminRoute>
            <FeatureRoute feature="gbPuntos" fallback="/admin"><AdminGBPuntos /></FeatureRoute>
          </AdminRoute>
        }
      />
      
      {/* Cambio de contraseña (ambos pueden acceder) */}
      <Route
        path="/change-password"
        element={
          <PrivateRoute>
            <ChangePassword />
          </PrivateRoute>
        }
      />

      {/* Perfil del usuario */}
      <Route
        path="/profile"
        element={
          <ClientRoute>
            <ProfileApp />
          </ClientRoute>
        }
      />
      
      {/* Ruta por defecto */}
      <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [splashShown, setSplashShown] = useState(false);

  useEffect(() => {
    const hasShownSplash = sessionStorage.getItem('girosbol_splash_shown');
    if (hasShownSplash) {
      setShowSplash(false);
      setSplashShown(true);
    }
  }, []);

  const handleSplashFinish = () => {
    setShowSplash(false);
    setSplashShown(true);
    sessionStorage.setItem('girosbol_splash_shown', 'true');
  };

  return (
    <AuthProvider>
      <BrowserRouter>
        {showSplash && !splashShown && (
          <SplashScreen onFinish={handleSplashFinish} />
        )}
        <DynamicManifest />
        <ConnectionBanner />
        <AppRoutes />
        <SmartWhatsApp />
        {/* Prompt "Instalar app" desactivado por pedido del cliente (feb 2026). */}
        {/* <InstallPWA /> */}
      </BrowserRouter>
    </AuthProvider>
  );
}

// Global reconnection banner - shows at top when server is down
const ConnectionBanner = () => {
  const { connectionError, user } = useAuth();
  if (!connectionError || !user) return null;
  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[9999] bg-yellow-500 text-yellow-900 text-center py-1.5 px-4 text-xs font-semibold animate-pulse shadow-md"
      data-testid="connection-banner"
    >
      Reconectando al servidor...
    </div>
  );
};

// WhatsApp floating button: visible en TODAS las páginas públicas y de cliente
// (landing, login, dashboard, profile, send, etc.).
// Se OCULTA en:
//  - panel admin (/admin/*) y módulo Empresas B2B (/empresas/*) — Carlos jul 3, 2026.
//  - flujos de tarea concentrada (registro, KYC, eliminar cuenta) — Carlos jul 23, 2026:
//    tapaban botones reales en móvil (ej. "Volver a escanear documento"). En estas
//    pantallas el usuario está en una tarea, no buscando soporte.
// Dentro de la app (con barra inferior) va COMPACTO y elevado.
const APP_ROUTES_WITH_BOTTOM_NAV = ['/dashboard', '/send-money', '/gb-puntos', '/transactions', '/profile', '/credibol'];
const ROUTES_HIDE_WHATSAPP = [
  '/register',
  '/verify-email',
  '/kyc-verify',
  '/kyc-2026-link',
  '/mi-biometria',
  '/eliminar-cuenta',
  '/eliminar-datos',
  '/delete-account',
  '/change-password',
  // Perfil cliente: el widget flotante tapa "Cerrar sesión" y otras acciones
  // (Carlos feb 2026). En una pantalla de ajustes el usuario no busca soporte.
  '/profile',
];
const SmartWhatsApp = () => {
  const location = useLocation();
  const path = location.pathname;
  const hidden =
    path.startsWith('/admin') ||
    path.startsWith('/empresas') ||
    ROUTES_HIDE_WHATSAPP.some((p) => path === p || path.startsWith(p + '/'));
  if (hidden) return null;
  const raised = APP_ROUTES_WITH_BOTTOM_NAV.some((p) => path.startsWith(p));
  return <WhatsAppButton raised={raised} />;
};

export default App;
